import { Grid, Typography, Tooltip, IconButton, RadioGroup, FormControlLabel, Radio, TextField, Fab } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import React, { useContext, useEffect, useState } from 'react'
import { StringsContext, useStrings } from '../../assets/localization/strings'
import { useFormContext, useWatch } from 'react-hook-form'
import { useReadonly } from '../../reusableUtils/useReadonly'
import { useCustomFieldArray } from '../../reusableUtils/useCustomFieldArray'
import { useParams } from 'react-router-dom'
import type { IUGCCampaign } from './usePersistedUGCForm'
import { Service } from '../../models/GeneralTypes'
import { theme } from '../../theme'
import ReactHookFormDropzone from '../common/form/ReactHookFormDropzone'
import TrashIcon from '../../assets/icons/trash_icon.svg'
import MockImage from '../../assets/images/mock_image.png'

const CampaignPreferences = () => {
  const strings = useStrings()
  const isReadonly = useReadonly('productPreferences')
  const { control, getValues } = useFormContext<IUGCCampaign>()
  const [useMoodboard, setUseMoodboard] = useState(!!getValues('moodboard').length)
  const { type } = useParams<{ type: 'update' | 'view' }>()
  const service = useWatch({ control, name: 'service' })
  const params = useParams<{ type: 'update' | 'view' }>()
  const { fields, append, remove, update } = useCustomFieldArray({ control, name: 'productPreferences', keyName: 'id' })
  const [hasProductPreference, setHasProductPreference] = useState<'true' | 'false'>('false')
  const { language } = useContext(StringsContext)

  const showScenes = service == Service.selfService || (service == Service.nanoService && (type == 'update' || type == 'view'))
  const showMoodboard = !service || showScenes || type == 'update' || type == 'view'

  useEffect(() => {
    const productPreferences = getValues('productPreferences')
    if (productPreferences && productPreferences.length > 0 && (params.type == 'view' || params.type == 'update')) {
      setHasProductPreference('true')
    }
  })
  return (
    <>
      <Grid item xs={12} mt={4}>
        <Typography sx={{ fontFamily: 'Bricolage Grotesque', fontWeight: 500, fontSize: '18px' }}>
          {strings.campaign_creation_preferences}
          <Tooltip title={strings.campaign_creation_preferences_tooltip}>
            <IconButton>
              <InfoOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Typography>
      </Grid>
      <Grid container sx={{ borderRadius: '16px', borderStyle: 'solid', borderColor: '#0000001A', py: '18px', px: '30px' }}>
        <Grid sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Grid container xs={12}>
            <Grid item xs={12} mb={'30px'}>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, textAlign: 'left' }}>{strings.campaign_preferences_description}</Typography>
            </Grid>
            <Grid item xs={12} mt={2}>
              <RadioGroup value={hasProductPreference} onChange={e => setHasProductPreference(e.target.value as 'true' | 'false')}>
                <FormControlLabel
                  value='false'
                  disabled={isReadonly}
                  control={<Radio />}
                  label={strings.campaign_creation_preferences_no}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', fontWeight: 400 } }}
                />
                <FormControlLabel
                  value='true'
                  control={<Radio />}
                  label={strings.campaign_creation_preferences_yes}
                  sx={{ [theme.breakpoints.down('sm')]: { marginBottom: '30px' }, '& .MuiFormControlLabel-label': { fontSize: '14px', fontWeight: 400 } }}
                  disabled={isReadonly}
                />
              </RadioGroup>
            </Grid>
          </Grid>
          {hasProductPreference === 'true' && (
            <Grid container xs={8} alignItems={'center'} justifyContent={'center'}>
              <img
                src={
                  language.startsWith('en')
                    ? 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/Mockup_UK_2.png'
                    : 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/Mockup-1.png'
                }
                alt='phone mock image'
                width={'375px'}
                style={{ margin: 'auto' }}
              />
            </Grid>
          )}
        </Grid>
        {hasProductPreference === 'true' && (
          <>
            {fields.map((field, i) => (
              <Grid container mt={2} alignItems='center' key={i}>
                <Grid item xs={11}>
                  <TextField
                    value={field.value}
                    onChange={e => update(i, { ...field, value: e.target.value })}
                    label={strings.campaign_creation_preferences_question(i + 1)}
                    type='outlined'
                    fullWidth
                    size='small'
                    placeholder={strings.campaign_creation_preferences_placeh}
                    InputLabelProps={{ shrink: true }}
                    disabled={isReadonly}
                  />
                </Grid>
                {!isReadonly && fields.length > 1 && (
                  <div
                    style={{
                      backgroundColor: '#FF2B001A',
                      padding: '8px',
                      marginLeft: '8px',
                      borderRadius: '10px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => remove(i)}>
                    <img src={TrashIcon} alt='delete icon' />
                  </div>
                )}
              </Grid>
            ))}
            {!isReadonly && fields.length < 3 && (
              <Grid item xs={12} textAlign='center' mt={2}>
                <Fab color='primary' aria-label='add' size='small' onClick={() => append({ id: `${fields.length}`, value: '' })} disabled={isReadonly}>
                  <AddIcon />
                </Fab>
              </Grid>
            )}
          </>
        )}
      </Grid>
      {showMoodboard && (
        <>
          <Grid item xs={12} mt={'16px'} mb={'12px'}>
            <Typography sx={{ fontFamily: 'Bricolage Grotesque', fontWeight: 500, fontSize: '18px' }}>{strings.campaign_creation_content_moodboard_title}</Typography>
          </Grid>
          <Grid container sx={{ borderRadius: '16px', borderStyle: 'solid', borderColor: '#0000001A', py: '18px', px: '30px' }}>
            <Grid item xs={12} mt={2}>
              <Typography variant='body2'>{strings.campaign_creation_content_moodboard_body}</Typography>
            </Grid>
            <Grid item xs={12} mt={2}>
              <RadioGroup value={useMoodboard} onChange={e => setUseMoodboard(e.target.value == 'true')}>
                <FormControlLabel
                  value={false}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', fontWeight: 400 } }}
                  control={<Radio />}
                  label={strings.campaign_creation_content_moodboard_dont_use}
                  disabled={isReadonly}
                />
                <FormControlLabel
                  value={true}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', fontWeight: 400 } }}
                  control={<Radio />}
                  label={strings.campaign_creation_content_moodboard_use}
                  disabled={isReadonly}
                />
              </RadioGroup>
            </Grid>
            {useMoodboard && (
              <>
                <Grid item xs={12} mt={2}>
                  <Typography variant='body2'>{strings.campaign_creation_content_moodboard_body1}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='body2'>{strings.campaign_creation_content_moodboard_body2}</Typography>
                </Grid>
                <Grid item xs={12} mt={2}>
                  <ReactHookFormDropzone name='moodboard' mode='append' accept='image/jpeg, image/png, video/mp4' />
                </Grid>
              </>
            )}
          </Grid>
        </>
      )}
    </>
  )
}

export default CampaignPreferences
